import { Collapse, ConfigProvider, Flex, type CollapseProps } from "antd";
import { CaretRightIcon } from "@phosphor-icons/react";
import { FontFamily, FontItem } from "../types";
import { FontItemComponent } from "./FontItemComponent";
import { FontPreview } from "./FontPreview";
import { getRowKey } from "../../storyboardEditor/helpers";
import { DeleteButton } from "components/DeleteButton/";

interface CollapseIconProps {
  isActive: boolean;
}

const CollapseIcon = ({ isActive }: CollapseIconProps) => {
  return (
    <CaretRightIcon
      size={15}
      weight="bold"
      style={{ transform: isActive ? "rotate(90deg)" : "rotate(0deg)" }}
    />
  );
};

interface FontFamilyCollapseProps {
  family: FontFamily;
  isExpanded: boolean;
  onToggle: (familyName: string, keys: string[]) => void;
  selectedFont?: string;
  selectedBackgroundColor: string;
  onFontSelect: (fontItem: FontItem) => void;
  id: string;
  assetLibraryId: string;
  allowDelete?: boolean;
  onDeleteSuccess?: (fontItemId: string) => void;
}

export const FontFamilyCollapse = ({
  family,
  isExpanded,
  onToggle,
  selectedFont,
  selectedBackgroundColor,
  onFontSelect,
  id,
  assetLibraryId,
  allowDelete = false,
  onDeleteSuccess,
}: FontFamilyCollapseProps) => {
  const handleFamilyToggle = (keys: string[]) => {
    onToggle(id, keys);
  };

  const renderFontItem = (variant: FontItem, showWeight: boolean) => {
    const fontItemId = getRowKey(variant.font);
    return (
      <Flex
        key={variant.key}
        align="center"
        justify="space-between"
        style={{ width: "100%" }}
      >
        <FontItemComponent
          variant={variant}
          showWeight={showWeight}
          isSelected={selectedFont === variant.key}
          selectedBackgroundColor={selectedBackgroundColor}
          onSelect={onFontSelect}
          style={{
            width: allowDelete ? "80%" : "100%",
          }}
        />
        {allowDelete && (
          <DeleteButton
            recordItemId={fontItemId}
            resource={`media/asset_libraries/${assetLibraryId}/assets`}
            onSuccess={() => {
              onDeleteSuccess?.(fontItemId);
            }}
          />
        )}
      </Flex>
    );
  };

  // If there's only one variant, show it directly without collapse
  if (family.variants.length === 1) {
    const variant = family.representativeVariant || family.variants[0];
    return renderFontItem(variant, false);
  }
  const collapseItems: CollapseProps["items"] = [
    {
      key: id,
      label: family.representativeVariant ? (
        <div style={{ width: "fit-content", maxWidth: "100%" }}>
          <FontPreview
            fontItem={family.representativeVariant}
            showWeight={false}
            style={{ fontSize: 14, padding: 0, textAlign: "left" }}
          />
        </div>
      ) : (
        family.familyName
      ),
      children: (
        <div style={{ marginTop: -10 }}>
          {family.variants.map((variant) => renderFontItem(variant, true))}
        </div>
      ),
    },
  ];

  return (
    <ConfigProvider
      theme={{
        components: {
          Collapse: {
            headerPadding: "4px 0",
            contentPadding: "0 24px",
          },
        },
      }}
    >
      <Collapse
        key={id}
        activeKey={isExpanded ? [id] : []}
        onChange={handleFamilyToggle}
        expandIcon={({ isActive }) => <CollapseIcon isActive={isActive} />}
        expandIconPosition="start"
        ghost
        items={collapseItems}
        data-testid={`font-family-${family.familyName.toLowerCase().replace(/\s+/g, "-")}`}
      />
    </ConfigProvider>
  );
};
